import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

export const EMPTY_FILTERS = {
  q: "",
  grade: "",
  subject: "",
  county: "",
  category: "",
  organizer: "",
  modes: [],
  prices: [],
  date_from: "",
  date_to: "",
};

export const GRADES = [
  "Predškolski odgoj",
  "Razredna nastava (1.–4.)",
  "Predmetna nastava (5.–8.)",
  "Srednja škola",
  "Ostalo / svi profili",
];

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{children}</p>
);

const FilterSelect = ({ testId, placeholder, value, options, onChange }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testId} className="w-full bg-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o} value={o}>
          {o}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const CheckGroup = ({ options, selected, onToggle, prefix }) => (
  <div className="flex flex-col gap-2.5">
    {options.map((o) => (
      <div key={o} className="flex items-center gap-2.5">
        <Checkbox
          id={`${prefix}-${o}`}
          data-testid={`filter-${prefix}-${o.toLowerCase()}`}
          checked={selected.includes(o)}
          onCheckedChange={() => onToggle(o)}
        />
        <Label htmlFor={`${prefix}-${o}`} className="text-sm text-slate-600 font-normal cursor-pointer">
          {o}
        </Label>
      </div>
    ))}
  </div>
);

export const Filters = ({ data, filters, onChange, onClear }) => {
  const set = (key, value) => onChange({ ...filters, [key]: value });
  const toggle = (key, value) =>
    set(key, filters[key].includes(value) ? filters[key].filter((v) => v !== value) : [...filters[key], value]);

  return (
    <div data-testid="filters-panel" className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col gap-6">
      <div>
        <SectionLabel>Razred / uzrast</SectionLabel>
        <FilterSelect
          testId="filter-grade"
          placeholder="Svi razredi"
          value={filters.grade}
          options={GRADES}
          onChange={(v) => set("grade", v)}
        />
      </div>

      <div>
        <SectionLabel>Predmet / područje</SectionLabel>
        <FilterSelect
          testId="filter-subject"
          placeholder="Svi predmeti"
          value={filters.subject}
          options={data.subjects || []}
          onChange={(v) => set("subject", v)}
        />
      </div>

      <div>
        <SectionLabel>Način održavanja</SectionLabel>
        <CheckGroup options={["Online", "Uživo"]} selected={filters.modes} onToggle={(v) => toggle("modes", v)} prefix="mode" />
      </div>

      <div>
        <SectionLabel>Županija</SectionLabel>
        <FilterSelect
          testId="filter-county"
          placeholder="Sve županije"
          value={filters.county}
          options={data.counties || []}
          onChange={(v) => set("county", v)}
        />
      </div>

      <div>
        <SectionLabel>Organizator</SectionLabel>
        <Select value={filters.organizer || "all"} onValueChange={(v) => set("organizer", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-organizer" className="w-full bg-white">
            <SelectValue placeholder="Svi organizatori" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Svi organizatori</SelectItem>
            {(data.organizers || []).map((o) => (
              <SelectItem key={o.name} value={o.name}>
                <span className="inline-flex items-center gap-2">
                  {o.name}
                  {o.count === 0 && (
                    <span
                      className="w-2 h-2 rounded-full bg-red-500 shrink-0"
                      title="Trenutno nema aktivnih edukacija"
                      data-testid={`organizer-inactive-dot-${o.name}`}
                    />
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block shrink-0" /> = trenutno bez aktivnih edukacija
        </p>
      </div>

      <div>
        <SectionLabel>Vrsta skupa</SectionLabel>
        <FilterSelect
          testId="filter-category"
          placeholder="Sve vrste"
          value={filters.category}
          options={data.categories || []}
          onChange={(v) => set("category", v)}
        />
      </div>

      <div>
        <SectionLabel>Cijena</SectionLabel>
        <CheckGroup
          options={["Besplatno", "Plaćeno"]}
          selected={filters.prices}
          onToggle={(v) => toggle("prices", v)}
          prefix="price"
        />
      </div>

      <div>
        <SectionLabel>Datum održavanja</SectionLabel>
        <div className="flex flex-col gap-2">
          <Input
            data-testid="filter-date-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => set("date_from", e.target.value)}
            className="bg-white"
          />
          <Input
            data-testid="filter-date-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => set("date_to", e.target.value)}
            className="bg-white"
          />
        </div>
      </div>

      <Button
        data-testid="clear-filters-btn"
        variant="outline"
        onClick={onClear}
        className="w-full gap-2 text-slate-600 bg-white"
      >
        <RotateCcw className="w-4 h-4" />
        Očisti filtere
      </Button>
    </div>
  );
};
